import { collabStateManager } from '@databases/collab-state-manager';
import {
  subscribeViewIdentityChanges,
  type ViewIdentityChanges,
} from '@databases/database-state';

import { type CurrentViewIdentity } from './get-current-view-layout-type.ts';

/**
 * Bootstrap-owned observation of the active renderer identity. The view-state
 * snapshot erases extension layout IDs, so persisted changes also invalidate it.
 * Start after SDK setup; dispose before replacing the database's modules.
 */
export function subscribeActiveViewIdentityChanges(
  contentId: string,
  onChange: (changes: ViewIdentityChanges) => void,
  readIdentity: (contentId: string) => CurrentViewIdentity,
): VoidFunction {
  let previous = readIdentity(contentId);
  let stopDocumentObservation: VoidFunction | undefined;

  const checkIdentity = () => {
    const next = readIdentity(contentId);
    const changes: ViewIdentityChanges = {
      viewIdChanged: previous.id !== next.id,
      layoutTypeChanged: previous.layoutType !== next.layoutType,
    };

    if (changes.viewIdChanged || changes.layoutTypeChanged) {
      previous = next;
      onChange(changes);
    }
  };

  const stopViewObservation = subscribeViewIdentityChanges(checkIdentity);
  const stopAttachmentObservation = collabStateManager.onCollabManagerAttached(
    contentId,
    (collabManager) => {
      stopDocumentObservation?.();

      const document = collabManager.database.doc;

      document.on('afterTransaction', checkIdentity);
      stopDocumentObservation = () => document.off('afterTransaction', checkIdentity);
      checkIdentity();
    },
  );

  let stopped = false;

  return () => {
    if (stopped) {
      return;
    }

    stopped = true;
    stopAttachmentObservation();
    stopViewObservation();
    stopDocumentObservation?.();
  };
}
